import React, { memo } from 'react';

import cls from 'classnames';

import _noop from 'lodash/noop';

import Checkbox from '@ott/checkbox';
import CheckIcon from '@ott/icon-check';

import { SelectProps } from '../../Select';
import { SIZE } from '../../helpers/constants';
import { Size } from '../../helpers/types';

import styles from './Option.scss';

export type SelectOption = {
  isOptionGroup?: false;
  className?: string;
  isDisabled?: boolean;
  value: number | string;
  label: string;
};

export type OptionProps<Option extends SelectOption = SelectOption> = {
  option: Option;
  isActive: boolean;
  isHovered: boolean;
  withCheckbox?: boolean;
  specificLocator: Required<SelectProps['specificLocator']>;
  size: Size;
};

function Option<Option extends SelectOption = SelectOption>({
  option,
  withCheckbox = false,
  isHovered = false,
  isActive,
  specificLocator,
  size,
}: OptionProps<Option>) {
  const { className, isDisabled, value, label } = option;

  return (
    <div
      className={cls(styles.option, className, {
        [styles.isDisabled]: isDisabled,
        [styles.isHovered]: isHovered,
        [styles.isSmall]: size === SIZE.SMALL,
      })}
      data-locator={cls(
        `${specificLocator}-option-${value}`,
        isDisabled && 'disabled',
        isActive && 'active',
        isHovered && 'hovered'
      )}
    >
      {withCheckbox && (
        <Checkbox
          className={styles.checkbox}
          onChange={_noop}
          isDisabled={isDisabled}
          isChecked={isActive}
          specificLocators={[`${specificLocator}-option-checkbox`]}
        >
          <span className={styles.overflowEllipsis}>{label}</span>
        </Checkbox>
      )}
      {!withCheckbox && (
        <>
          <div className={cls(styles.label)}>{label}</div>
          <CheckIcon className={cls(styles.checkIcon, { [styles.isHidden]: !isActive })} />
        </>
      )}
    </div>
  );
}

export default memo(Option);
